import { Plus, Trash2 } from 'lucide-react';
import type { IssuedInvoiceLineInput } from '../../../lib/issuedInvoicesApi';
import { computeLine } from '../../../lib/invoiceMath';
import { useUnitOptions } from '../../../hooks/useIssuedInvoices';
import { Select } from '../../ui/Select';

export const VAT_RATE_OPTIONS = [
  { value: '21', label: '21%' },
  { value: '11', label: '11%' },
  { value: '5', label: '5%' },
  { value: '0', label: '0%' },
];

export function emptyLine(): IssuedInvoiceLineInput {
  return { name: '', description: '', unit: 'H87', quantity: 1, unitPrice: 0, vatRate: 21 };
}

interface Props {
  lines: IssuedInvoiceLineInput[];
  pricesIncludeVat: boolean;
  sellerVatPayer: boolean;
  onChange: (lines: IssuedInvoiceLineInput[]) => void;
  disabled?: boolean;
  /** A storno gives back what was invoiced, so its quantities are negative. */
  allowNegativeQuantity?: boolean;
}

const cell =
  'w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50';

function fmt(v: number): string {
  return new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

/**
 * The lines table of the invoice form: name + details, unit, quantity, unit
 * price, VAT rate, and a live line total computed with the same maths the
 * backend persists (`lib/invoiceMath.ts`).
 */
export function IssuedInvoiceLinesEditor({
  lines,
  pricesIncludeVat,
  sellerVatPayer,
  allowNegativeQuantity = false,
  onChange,
  disabled,
}: Props) {
  const units = useUnitOptions();
  const unitOptions = (units.data ?? []).map((u) => ({ value: u.code, label: u.label }));

  function patch(i: number, p: Partial<IssuedInvoiceLineInput>) {
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  }

  function remove(i: number) {
    onChange(lines.filter((_, idx) => idx !== i));
  }

  const amountsOf = (line: IssuedInvoiceLineInput) =>
    computeLine(
      {
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        vatRate: sellerVatPayer ? line.vatRate : 0,
        vatCategory: sellerVatPayer ? undefined : 'O',
      },
      pricesIncludeVat,
    );

  return (
    <div className="space-y-2">
      {/* Phone: one card per line, fields stacked — the table below needs
          720px and was cutting the price column off the screen. */}
      <div className="space-y-3 md:hidden">
        {lines.map((line, i) => {
          const amounts = amountsOf(line);
          return (
            <div key={i} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Linia {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  disabled={disabled || lines.length === 1}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                  aria-label="Șterge linia"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <label className="mt-2 block text-xs font-medium text-gray-600">
                Denumire produs / serviciu
                <input
                  className={`${cell} mt-1`}
                  placeholder="ex. Servicii de dezvoltare software la comandă"
                  value={line.name}
                  disabled={disabled}
                  onChange={(e) => patch(i, { name: e.target.value })}
                />
              </label>
              <input
                className={`${cell} mt-2 text-xs text-gray-600`}
                placeholder="Detalii linie (opțional)"
                value={line.description ?? ''}
                disabled={disabled}
                onChange={(e) => patch(i, { description: e.target.value })}
              />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Select
                  label="UM"
                  size="sm"
                  value={line.unit}
                  onChange={(v) => patch(i, { unit: v })}
                  options={unitOptions}
                  disabled={disabled}
                  loading={units.isLoading}
                />
                <label className="block text-xs font-medium text-gray-600">
                  Cantitate
                  <input
                    className={`${cell} mt-1 text-right`}
                    type="number"
                    inputMode="decimal"
                    min={allowNegativeQuantity ? undefined : 0}
                    step="0.001"
                    value={line.quantity}
                    disabled={disabled}
                    onChange={(e) => patch(i, { quantity: Number(e.target.value) })}
                  />
                </label>
                <label className="block text-xs font-medium text-gray-600">
                  Preț unitar {pricesIncludeVat ? '(cu TVA)' : '(fără TVA)'}
                  <input
                    className={`${cell} mt-1 text-right`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={line.unitPrice}
                    disabled={disabled}
                    onChange={(e) => patch(i, { unitPrice: Number(e.target.value) })}
                  />
                </label>
                {sellerVatPayer && (
                  <Select
                    label="TVA"
                    size="sm"
                    value={String(line.vatRate)}
                    onChange={(v) => patch(i, { vatRate: Number(v) })}
                    options={VAT_RATE_OPTIONS}
                    disabled={disabled}
                  />
                )}
              </div>
              <div className="mt-3 flex items-baseline justify-between border-t border-gray-100 pt-2 tabular-nums">
                <span className="text-xs text-gray-500">
                  {sellerVatPayer
                    ? `${fmt(amounts.netAmount)} + TVA ${fmt(amounts.vatAmount)}`
                    : 'Total linie'}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {fmt(amounts.grossAmount)} lei
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-2">#</th>
              <th className="py-2 pr-2">Denumire produs / serviciu</th>
              <th className="py-2 pr-2 w-28">UM</th>
              <th className="py-2 pr-2 w-24">Cantitate</th>
              <th className="py-2 pr-2 w-32">
                Preț unitar {pricesIncludeVat ? '(cu TVA)' : '(fără TVA)'}
              </th>
              {sellerVatPayer && <th className="py-2 pr-2 w-24">TVA</th>}
              <th className="py-2 pr-2 w-32 text-right">Total linie</th>
              <th className="py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const amounts = amountsOf(line);
              return (
                <tr key={i} className="border-b border-gray-100 align-top">
                  <td className="py-2 pr-2 text-gray-500">{i + 1}</td>
                  <td className="py-2 pr-2">
                    <input
                      className={cell}
                      placeholder="ex. Servicii de dezvoltare software la comandă"
                      value={line.name}
                      disabled={disabled}
                      onChange={(e) => patch(i, { name: e.target.value })}
                    />
                    <input
                      className={`${cell} mt-1 text-xs text-gray-600`}
                      placeholder="Detalii linie (opțional)"
                      value={line.description ?? ''}
                      disabled={disabled}
                      onChange={(e) => patch(i, { description: e.target.value })}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <Select
                      label="UM"
                      hideLabel
                      size="sm"
                      width={140}
                      value={line.unit}
                      onChange={(v) => patch(i, { unit: v })}
                      options={unitOptions}
                      disabled={disabled}
                      loading={units.isLoading}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      className={`${cell} text-right`}
                      type="number"
                      min={allowNegativeQuantity ? undefined : 0}
                      step="0.001"
                      value={line.quantity}
                      disabled={disabled}
                      onChange={(e) => patch(i, { quantity: Number(e.target.value) })}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      className={`${cell} text-right`}
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitPrice}
                      disabled={disabled}
                      onChange={(e) => patch(i, { unitPrice: Number(e.target.value) })}
                    />
                  </td>
                  {sellerVatPayer && (
                    <td className="py-2 pr-2">
                      <Select
                        label="TVA"
                        hideLabel
                        size="sm"
                        width={90}
                        value={String(line.vatRate)}
                        onChange={(v) => patch(i, { vatRate: Number(v) })}
                        options={VAT_RATE_OPTIONS}
                        disabled={disabled}
                      />
                    </td>
                  )}
                  <td className="py-2 pr-2 text-right tabular-nums">
                    <div className="font-medium text-gray-900">{fmt(amounts.grossAmount)} lei</div>
                    {sellerVatPayer && (
                      <div className="text-xs text-gray-500">
                        {fmt(amounts.netAmount)} + TVA {fmt(amounts.vatAmount)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      disabled={disabled || lines.length === 1}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                      aria-label="Șterge linia"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={() => onChange([...lines, emptyLine()])}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
      >
        <Plus className="h-4 w-4" /> Adaugă linie
      </button>
    </div>
  );
}
